import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Property, Auction, Notification, FilterState, Bid, User, Chat, Message, SavedSearch } from '../types';
import { MOCK_USERS } from '../data/mockData';
import { client, toggleFavorite as apiToggleFavorite, getFavorites as apiGetFavorites, getProperties as apiGetProperties } from '../api/client';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  tokenBalance?: number;
  verified?: boolean;
  emailVerified?: boolean;
  role?: string;
  type?: 'fizicko_lice' | 'pravno_lice';
}

interface AppState {
  // Auth state (Youbase)
  user: AuthUser | null;
  isAuthenticated: boolean;
  isChecking: boolean;
  tokenBalance: number;
  
  // Properties (lokalni cache)
  properties: Property[];
  demands: Property[];
  auctions: Auction[];
  propertiesLoading: boolean;
  propertiesLoaded: boolean;
  
  // Filters
  filters: FilterState;
  
  // Notifications
  notifications: Notification[];
  unreadCount: number;
  
  // Chats
  chats: Chat[];

  // UI state
  language: 'sr' | 'en';
  theme: 'light' | 'dark';
  
  // Auth actions
  setUser: (user: AuthUser | null) => void;
  setChecking: (value: boolean) => void;
  setTokenBalance: (balance: number) => void;
  reset: () => void;
  
  // Filter actions
  setFilters: (filters: Partial<FilterState>) => void;
  clearFilters: () => void;
  
  // UI actions
  setLanguage: (lang: 'sr' | 'en') => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  markNotificationRead: (id: string) => void;
  setNotifications: (notifications: Notification[]) => void;
  loadNotifications: () => Promise<void>;
  
  // Chat actions
  sendPropertyMessage: (propertyId: string, senderId: string, content: string, subject?: string) => void;
  getChats: (userId: string) => Chat[];
  markChatRead: (chatId: string, userId: string) => void;

  // Filter getters
  getFilteredProperties: () => Property[];
  getFilteredDemands: () => Property[];
  getFilteredAuctions: () => Auction[];
  
  // Property actions (lokalni cache)
  setProperties: (properties: Property[]) => void;
  setDemands: (demands: Property[]) => void;
  setAuctions: (auctions: Auction[]) => void;
  addProperty: (property: Property) => void;
  updateProperty: (id: string, updates: Partial<Property>) => void;
  deleteProperty: (id: string | number) => Promise<boolean>;
  loadPropertiesFromApi: () => Promise<void>;
  
  // Auction actions
  placeBid: (auctionId: string, bid: Bid) => void;
  
  // Token actions (lokalni cache - sinhronizuje se sa backendom)
  addTokens: (amount: number) => void;
  deductTokens: (amount: number) => boolean;
  redeemCode: (code: string) => boolean;
  
  // Admin actions
  users: User[];
  fetchUsers: () => Promise<void>;
  getAllUsers: () => User[];
  approveProperty: (id: string) => void;
  rejectProperty: (id: string) => void;
  suspendProperty: (id: string, reason: string) => void;
  suspendUser: (id: string) => void;
  unsuspendUser: (id: string) => void;
  sendMessage: (userId: string, message: string) => void;
  manageTokens: (userId: string, amount: number) => void;
  blockUserAI: (userId: string) => void;
  reportAbuse: (userId: string, reason: string, details: string) => Promise<void>;
  addNotification: (notification: Notification) => void;
  updateUser: (id: string, updates: Partial<User>) => void;
  incrementViews: (id: string) => Promise<void>;
  
  // Favorites actions
  favorites: string[];
  setFavorites: (favorites: string[]) => void;
  addToFavorites: (propertyId: string) => void;
  removeFromFavorites: (propertyId: string) => void;
  toggleFavorite: (propertyId: string) => void;
  isFavorite: (propertyId: string) => boolean;
  
  // Saved Searches
  savedSearches: SavedSearch[];
  saveSearch: (name: string, pageType?: 'offers' | 'demands' | 'auctions') => void;
  loadSavedSearches: () => Promise<void>;
  deleteSavedSearch: (id: string) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isChecking: true,
      tokenBalance: 0,
      properties: [],
      demands: [],
      auctions: [],
      propertiesLoading: false,
      propertiesLoaded: false,
      filters: {},
      notifications: [],
      unreadCount: 0,
      chats: [],
      language: 'sr',
      theme: 'light',
      favorites: [],
      savedSearches: [],
      
      // Auth actions
      setUser: (user) => set({ 
        user, 
        isAuthenticated: !!user,
        isChecking: false 
      }),
      
      setChecking: (value) => set({ isChecking: value }),
      
      setTokenBalance: (balance) => set({ tokenBalance: balance }),
      
      reset: () => set({ 
        user: null, 
        isAuthenticated: false, 
        isChecking: false,
        tokenBalance: 0 
      }),
      
      // Filter actions
      setFilters: (newFilters) => set((state) => ({
        filters: { ...state.filters, ...newFilters }
      })),
      
      clearFilters: () => set({ filters: {} }),
      
      // UI actions
      setLanguage: (language) => set({ language }),
      
      setTheme: (theme) => {
        set({ theme });
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },
      
      toggleTheme: () => {
        const { theme } = get();
        const newTheme = theme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        if (newTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },
      
      markNotificationRead: (id) => set((state) => ({
        notifications: state.notifications.map(n => 
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      })),

      setNotifications: (notifications) => set({
        notifications,
        unreadCount: notifications.filter(n => !n.read).length,
      }),

      loadNotifications: async () => {
        // Notifikacije se učitavaju iz backend API-ja
        // Ovde bi se pozivao /api/notifications/email
        // Za sada ostaje prazno - notifikacije se učitavaju u komponenti
      },

      sendPropertyMessage: (propertyId, senderId, content, subject) => {
        set((state) => {
          const property = [...state.properties, ...state.auctions, ...state.demands].find(p => p.id === propertyId);
          if (!property) return state;

          const ownerId = property.userId;
          let chat = state.chats.find(c => 
            c.propertyId === propertyId && 
            c.participants.includes(senderId) && 
            c.participants.includes(ownerId)
          );

          const newMessage: Message = {
            id: Date.now().toString(),
            chatId: chat ? chat.id : Date.now().toString(),
            senderId,
            content,
            createdAt: new Date().toISOString(),
            read: false
          };

          let newChats = [...state.chats];

          if (chat) {
            chat = {
              ...chat,
              messages: [...chat.messages, newMessage],
              lastMessage: newMessage,
              updatedAt: new Date().toISOString()
            };
            newChats = newChats.map(c => c.id === chat!.id ? chat! : c);
          } else {
            chat = {
              id: Date.now().toString(),
              propertyId,
              participants: [senderId, ownerId],
              messages: [newMessage],
              lastMessage: newMessage,
              updatedAt: new Date().toISOString()
            };
            newChats.push(chat);
          }

          // Notify owner
          const notification: Notification = {
            id: Date.now().toString(),
            userId: ownerId,
            type: 'match',
            title: subject || 'Nova poruka',
            message: `Imate novu poruku za oglas "${property.title}"`,
            link: `/notifikacije?tab=poruke&chat=${chat.id}`,
            read: false,
            createdAt: new Date().toISOString()
          };

          return {
            chats: newChats,
            notifications: [notification, ...state.notifications],
            unreadCount: state.unreadCount + 1
          };
        });
      },

      getChats: (userId) => {
        const { chats } = get();
        return chats.filter(c => c.participants.includes(userId)).sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      },

      markChatRead: (chatId, userId) => {
        set((state) => ({
          chats: state.chats.map(c => {
            if (c.id !== chatId) return c;
            return {
              ...c,
              messages: c.messages.map(m => 
                m.senderId !== userId ? { ...m, read: true } : m
              )
            };
          })
        }));
      },

      redeemCode: (_code) => {
        // Deprecated: Use redeemPromoCode from api/client.ts instead
        console.warn('redeemCode is deprecated. Use redeemPromoCode from api/client.ts');
        return false;
      },
      
      // Filter getters
      getFilteredProperties: () => {
        const { properties, filters } = get();
        const now = Date.now();
        const activeProperties = properties.filter(p => 
          p.status === 'active' && p.approved && 
          (!p.expiresAt || new Date(p.expiresAt).getTime() > now)
        );
        return filterProperties(activeProperties, filters);
      },
      
      getFilteredDemands: () => {
        const { demands, filters } = get();
        const now = Date.now();
        const activeDemands = demands.filter(p => 
          p.status === 'active' && p.approved &&
          (!p.expiresAt || new Date(p.expiresAt).getTime() > now)
        );
        return filterProperties(activeDemands, filters);
      },
      
      getFilteredAuctions: () => {
        const { auctions, filters } = get();
        const now = Date.now();
        const activeAuctions = auctions.filter(a => 
          a.status === 'active' && a.approved &&
          (!a.expiresAt || new Date(a.expiresAt).getTime() > now) &&
          (!a.auctionEndsAt || new Date(a.auctionEndsAt).getTime() > now)
        );
        return filterProperties(activeAuctions, filters) as Auction[];
      },
      
      // Property actions
      setProperties: (properties) => set({ properties }),
      setDemands: (demands) => set({ demands }),
      setAuctions: (auctions) => set({ auctions }),
      
      addProperty: (property) => set((state) => {
        if (property.adKind === 'Potražnja') {
          return { demands: [property, ...state.demands] };
        } else if (property.adKind === 'Aukcija') {
          return { auctions: [property as Auction, ...state.auctions] };
        }
        return { properties: [property, ...state.properties] };
      }),
      
      updateProperty: (id, updates) => set((state) => ({
        properties: state.properties.map(p => p.id === id ? { ...p, ...updates } : p),
        demands: state.demands.map(p => p.id === id ? { ...p, ...updates } : p),
        auctions: state.auctions.map(a => a.id === id ? { ...a, ...updates } : a),
      })),
      
      deleteProperty: async (id) => {
        try {
          const response = await client.api.fetch(`/properties/${id}`, {
            method: 'DELETE'
          });
          
          if (response.ok) {
            const stringId = String(id);
            set((state) => ({
              properties: state.properties.filter(p => p.id !== stringId),
              demands: state.demands.filter(p => p.id !== stringId),
              auctions: state.auctions.filter(a => a.id !== stringId),
            }));
            return true;
          }
          return false;
        } catch (error) {
          console.error('Failed to delete property:', error);
          return false;
        }
      },
      
      loadPropertiesFromApi: async () => {
        // Prevent multiple simultaneous loads
        if (get().propertiesLoading) return;
        
        set({ propertiesLoading: true });
        
        try {
          // Load offers/sales/rent
          const offers = await apiGetProperties({ type: 'offer', limit: 100 });
          
          // Load demands
          const demands = await apiGetProperties({ type: 'demand', limit: 100 });
          
          // Load auctions
          const auctions = await apiGetProperties({ type: 'auction', limit: 100 });
          
          // Transform data to match frontend types
          const transformProperty = (p: any): Property => ({
            id: String(p.id),
            title: p.title || '',
            description: p.description || '',
            price: Number(p.price) || 0,
            currency: p.currency || 'EUR',
            adKind: p.ad_kind || 'Ponuda',
            adType: p.ad_type || 'Prodaja',
            category: p.category || 'Stan',
            subcategory: p.subcategory || '',
            squareMeters: Number(p.square_meters) || 0,
            rooms: Number(p.rooms) || 0,
            floor: p.floor ? Number(p.floor) : undefined,
            totalFloors: p.total_floors ? Number(p.total_floors) : undefined,
            heating: p.heating || p.heating_type || undefined,
            region: p.region || '',
            city: p.city || '',
            address: p.address || '',
            location: {
              region: p.region || '',
              city: p.city || '',
              address: p.address || '',
              lat: p.lat ? Number(p.lat) : undefined,
              lng: p.lng ? Number(p.lng) : undefined,
            },
            images: typeof p.images === 'string' ? JSON.parse(p.images || '[]') : (p.images || []),
            features: p.features || {},
            views: Number(p.views) || 0,
            contactViews: Number(p.contact_views) || 0,
            status: p.status || 'active',
            approved: Boolean(p.approved),
            featured: Boolean(p.featured),
            createdAt: p.created_at || new Date().toISOString(),
            expiresAt: p.expires_at,
            userId: String(p.user_id || ''),
            contactPhone: p.contact_phone || '',
            contactEmail: p.contact_email || '',
          });
          
          const transformAuction = (p: any): Auction => ({
            ...transformProperty(p),
            currentPrice: Number(p.current_bid) || Number(p.price) || 0,
            startingPrice: Number(p.starting_bid) || Number(p.price) || 0,
            auctionEndsAt: p.auction_end_date || p.expires_at || new Date().toISOString(),
            bids: [],
          });
          
          set({
            properties: offers.map(transformProperty),
            demands: demands.map(transformProperty),
            auctions: auctions.map(transformAuction),
            propertiesLoading: false,
            propertiesLoaded: true,
          });
        } catch (error) {
          console.error('Error loading properties from API:', error);
          set({ propertiesLoading: false });
        }
      },
      
      // Auction actions
      placeBid: (auctionId, bid) => set((state) => ({
        auctions: state.auctions.map(a => {
          if (a.id === auctionId) {
            return {
              ...a,
              currentPrice: bid.amount,
              bids: [...a.bids, bid],
            };
          }
          return a;
        }),
      })),
      
      // Token actions
      addTokens: (amount) => set((state) => ({
        tokenBalance: state.tokenBalance + amount,
      })),
      
      deductTokens: (amount) => {
        const { tokenBalance } = get();
        if (tokenBalance < amount) return false;
        set({ tokenBalance: tokenBalance - amount });
        return true;
      },

      // Admin actions
      users: [],
      
      fetchUsers: async () => {
        try {
          const res = await client.api.fetch('/api/admin/users');
          if (res.ok) {
            const data = await res.json();
            // PHP API returns { success: true, users: [...] }
            set({ users: data.users || data.data || [] });
          } else {
            console.error('Failed to fetch users - response not ok:', res.status);
          }
        } catch (error) {
          console.error('Failed to fetch users:', error);
        }
      },

      getAllUsers: () => get().users,
      
      approveProperty: (id) => {
        const { addNotification } = get();
        set((state) => {
          const updateStatus = (p: Property | Auction) => {
            if (p.id === id) {
              // Notify user
              addNotification({
                id: Date.now().toString(),
                userId: p.userId,
                type: 'admin',
                title: 'Oglas odobren',
                message: `Vaš oglas "${p.title}" je odobren i sada je javan.`,
                link: `/oglas/${p.id}`,
                read: false,
                createdAt: new Date().toISOString()
              });
              return { ...p, approved: true, status: 'active' as const };
            }
            return p;
          };
          return {
            properties: state.properties.map(updateStatus),
            demands: state.demands.map(updateStatus),
            auctions: state.auctions.map(updateStatus as any),
          };
        });
      },

      rejectProperty: (id) => {
        const { addNotification } = get();
        set((state) => {
          const updateStatus = (p: Property | Auction) => {
            if (p.id === id) {
              // Notify user
              addNotification({
                id: Date.now().toString(),
                userId: p.userId,
                type: 'admin',
                title: 'Oglas odbijen',
                message: `Vaš oglas "${p.title}" je odbijen.`,
                read: false,
                createdAt: new Date().toISOString()
              });
              return { ...p, approved: false, status: 'rejected' as const };
            }
            return p;
          };
          return {
            properties: state.properties.map(updateStatus),
            demands: state.demands.map(updateStatus),
            auctions: state.auctions.map(updateStatus as any),
          };
        });
      },

      suspendProperty: (id, reason) => {
        const { addNotification } = get();
        set((state) => {
          const updateStatus = (p: Property | Auction) => {
            if (p.id === id) {
              // Notify user
              addNotification({
                id: Date.now().toString(),
                userId: p.userId,
                type: 'admin',
                title: 'Oglas suspendovan',
                message: `Vaš oglas "${p.title}" je suspendovan. Razlog: ${reason}. Molimo vas izmenite oglas.`,
                link: `/izmeni-oglas/${p.id}`,
                read: false,
                createdAt: new Date().toISOString()
              });
              return { ...p, approved: false, status: 'suspended' as const, adminComments: reason };
            }
            return p;
          };
          return {
            properties: state.properties.map(updateStatus),
            demands: state.demands.map(updateStatus),
            auctions: state.auctions.map(updateStatus as any),
          };
        });
      },

      suspendUser: (id) => {
        const { updateUser, addNotification } = get();
        updateUser(id, { isSuspended: true });
        addNotification({
          id: Date.now().toString(),
          userId: id,
          type: 'admin',
          title: 'Nalog suspendovan',
          message: 'Vaš nalog je suspendovan od strane administratora.',
          read: false,
          createdAt: new Date().toISOString()
        });
      },

      unsuspendUser: (id) => {
        const { updateUser, addNotification } = get();
        updateUser(id, { isSuspended: false });
        addNotification({
          id: Date.now().toString(),
          userId: id,
          type: 'admin',
          title: 'Nalog aktiviran',
          message: 'Vaš nalog je ponovo aktivan.',
          read: false,
          createdAt: new Date().toISOString()
        });
      },

      sendMessage: (userId, message) => {
        const { addNotification } = get();
        addNotification({
          id: Date.now().toString(),
          userId: userId,
          type: 'admin',
          title: 'Poruka od administratora',
          message: message,
          read: false,
          createdAt: new Date().toISOString()
        });
      },

      manageTokens: (userId, amount) => {
        const { updateUser, addNotification, user: currentUser, setTokenBalance } = get();
        const user = MOCK_USERS.find(u => u.id === userId);
        if (user) {
          const newBalance = (user.tokens || 0) + amount;
          updateUser(userId, { tokens: newBalance });
          
          // If the updated user is the current logged in user, update the store state
          if (currentUser && currentUser.id === userId) {
            set({ user: { ...currentUser, tokenBalance: newBalance } });
            setTokenBalance(newBalance);
          }

          addNotification({
            id: Date.now().toString(),
            userId: userId,
            type: 'system',
            title: 'Promena stanja tokena',
            message: `Vaše stanje tokena je promenjeno za ${amount}. Novo stanje: ${newBalance}.`,
            read: false,
            createdAt: new Date().toISOString()
          });
        }
      },

      blockUserAI: (userId) => {
        const { updateUser, addNotification } = get();
        // Update user to block AI
        updateUser(userId, { isSuspended: true }); // Using isSuspended for now, or add specific flag
        
        // Notify Admin (admin1 is the main admin in mock data)
        addNotification({
          id: Date.now().toString(),
          userId: 'admin1', 
          type: 'admin',
          title: 'Zloupotreba AI Asistenta',
          message: `Korisnik ${userId} je blokiran zbog zloupotrebe AI asistenta.`,
          read: false,
          createdAt: new Date().toISOString()
        });

        // Notify User
        addNotification({
          id: Date.now().toString(),
          userId: userId,
          type: 'system',
          title: 'AI Asistent Blokiran',
          message: 'Vaš pristup AI asistentu je blokiran zbog kršenja pravila korišćenja.',
          read: false,
          createdAt: new Date().toISOString()
        });
      },

      reportAbuse: async (userId, reason, details) => {
        // Send abuse report to backend API - admin notification is created server-side
        try {
          await client.api.fetch('/api/reports/abuse', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              reportedUserId: userId,
              reason,
              details
            })
          });
        } catch (error) {
          console.error('Error reporting abuse:', error);
        }
      },

      addNotification: (notification) => set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1
      })),
      
      updateUser: (id, updates) => {
        // Update user in MOCK_USERS array (for demo purposes)
        const userIndex = MOCK_USERS.findIndex(u => u.id === id);
        if (userIndex !== -1) {
          Object.assign(MOCK_USERS[userIndex], updates);
        }
        console.log('[ADMIN] Update user:', id, updates);
      },

      incrementViews: async (id) => {
        // Optimistic local update
        set((state) => ({
          properties: state.properties.map(p => p.id === id ? { ...p, views: p.views + 1 } : p),
          demands: state.demands.map(p => p.id === id ? { ...p, views: p.views + 1 } : p),
          auctions: state.auctions.map(a => a.id === id ? { ...a, views: a.views + 1 } : a),
        }));
        
        // Call dedicated view increment endpoint
        try {
          await client.api.fetch(`/api/properties/${id}/view`, { method: 'POST' });
        } catch (error) {
          console.error('Error incrementing views:', error);
        }
      },
      
      // Favorites actions
      setFavorites: (favorites) => set({ favorites }),
      
      addToFavorites: (propertyId) => set((state) => {
        if (!state.favorites.includes(propertyId)) {
          return { favorites: [...state.favorites, propertyId] };
        }
        return state;
      }),
      
      removeFromFavorites: (propertyId) => set((state) => ({
        favorites: state.favorites.filter(id => id !== propertyId)
      })),
      
      toggleFavorite: async (propertyId) => {
        const { isAuthenticated, favorites } = get();
        
        if (!isAuthenticated) {
          console.warn('User must be logged in to toggle favorites');
          return;
        }
        
        // Optimistički update
        const isFav = favorites.includes(propertyId);
        set((state) => ({
          favorites: isFav 
            ? state.favorites.filter(id => id !== propertyId)
            : [...state.favorites, propertyId]
        }));
        
        // API poziv
        try {
          const result = await apiToggleFavorite(propertyId);
          if (!result.success) {
            // Rollback ako nije uspelo
            set((state) => ({
              favorites: isFav 
                ? [...state.favorites, propertyId]
                : state.favorites.filter(id => id !== propertyId)
            }));
          }
        } catch (error) {
          console.error('Toggle favorite error:', error);
          // Rollback
          set((state) => ({
            favorites: isFav 
              ? [...state.favorites, propertyId]
              : state.favorites.filter(id => id !== propertyId)
          }));
        }
      },
      
      isFavorite: (propertyId) => {
        return get().favorites.includes(propertyId);
      },
      
      // Saved Searches
      saveSearch: async (name, pageType = 'offers') => {
        const state = get();
        if (!state.user?.id) return;
        
        const newSearch: SavedSearch = {
          id: `search-${Date.now()}`,
          userId: state.user.id,
          name,
          filters: { ...state.filters },
          createdAt: new Date().toISOString(),
          pageType,
        };
        
        // Save to backend
        try {
          const response = await client.api.fetch('/saved-searches', {
            method: 'POST',
            body: JSON.stringify({
              name: newSearch.name,
              filters: newSearch.filters,
              page_type: pageType,
            }),
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              newSearch.id = String(data.data.id);
            }
          }
        } catch (error) {
          console.error('Failed to save search to backend:', error);
        }
        
        set((state) => ({
          savedSearches: [...state.savedSearches, newSearch],
        }));
      },
      
      loadSavedSearches: async () => {
        const state = get();
        if (!state.user?.id) return;
        
        try {
          const response = await client.api.fetch('/saved-searches');
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              const searches: SavedSearch[] = data.data.map((s: any) => ({
                id: String(s.id),
                userId: String(s.user_id),
                name: s.name,
                filters: s.filters || {},
                createdAt: s.created_at,
                pageType: s.page_type || 'offers',
              }));
              set({ savedSearches: searches });
            }
          }
        } catch (error) {
          console.error('Failed to load saved searches:', error);
        }
      },
      
      deleteSavedSearch: async (id) => {
        const state = get();
        if (!state.user?.id) return;
        
        try {
          await client.api.fetch(`/saved-searches/${id}`, {
            method: 'DELETE',
          });
        } catch (error) {
          console.error('Failed to delete saved search:', error);
        }
        
        set((state) => ({
          savedSearches: state.savedSearches.filter(s => s.id !== id),
        }));
      },
    }),
    {
      name: 'svojnasvom-storage',
      partialize: (state) => ({ 
        theme: state.theme, 
        language: state.language,
        user: state.user,
        favorites: state.favorites,
      }),
    }
  )
);

function filterProperties<T extends Property>(items: T[], filters: FilterState): T[] {
  return items.filter(item => {
    if (filters.adType && item.adType !== filters.adType) return false;
    if (filters.category && item.category !== filters.category) return false;
    if (filters.subcategory && item.subcategory !== filters.subcategory) return false;
    
    // Handle multiple locations filter
    if (filters.locations && filters.locations.length > 0) {
      const matchesLocation = filters.locations.some(loc => {
        if (loc.type === 'region') {
          return item.location.region === loc.name;
        } else if (loc.type === 'subregion') {
          return item.location.city === loc.name || item.location.region === loc.region;
        } else if (loc.type === 'listing-address') {
          // For listing-address, filter to the specific property ID
          if (loc.propertyId) {
            return item.id === loc.propertyId;
          }
          // Fallback: filter by subregion if no specific property match
          return loc.subregion ? item.location.city === loc.subregion : false;
        } else if (loc.type === 'address') {
          // For OSM address search without matching listing:
          // Filter by subregion (city/municipality) that the address belongs to
          if (loc.subregion) {
            // Check if any property is in that subregion
            return item.location.city === loc.subregion || 
                   item.location.city.toLowerCase().includes(loc.subregion.toLowerCase()) ||
                   loc.subregion.toLowerCase().includes(item.location.city.toLowerCase());
          }
          // Fallback: check if address text matches
          const itemAddress = `${item.location.address} ${item.location.city} ${item.location.region}`.toLowerCase();
          return itemAddress.includes(loc.name.split(',')[0].toLowerCase());
        }
        return false;
      });
      if (!matchesLocation) return false;
    } else {
      // Fallback to single region/city filter
      if (filters.region && item.location.region !== filters.region) return false;
      if (filters.city && item.location.city !== filters.city) return false;
    }
    
    if (filters.priceMin && item.price < filters.priceMin) return false;
    if (filters.priceMax && item.price > filters.priceMax) return false;
    if (filters.squareMetersMin && item.squareMeters < filters.squareMetersMin) return false;
    if (filters.squareMetersMax && item.squareMeters > filters.squareMetersMax) return false;
    
    // Rooms filter - range (roomsMin/roomsMax)
    if (item.rooms !== undefined && item.rooms !== null) {
      // roomsMin check
      if (filters.roomsMin !== undefined) {
        if (item.rooms < filters.roomsMin) return false;
      }
      // roomsMax check - 7+ means no upper limit
      if (filters.roomsMax !== undefined && filters.roomsMax < 7) {
        if (item.rooms > filters.roomsMax) return false;
      }
    }
    
    // Legacy rooms array filter for backwards compatibility
    if (filters.rooms?.length && item.rooms !== undefined) {
      const matchesRoom = filters.rooms.some(filterRoom => {
        if (filterRoom === 5 || filterRoom >= 7) {
          // 5+ or 7+ means 5/7 or more rooms
          return item.rooms! >= filterRoom;
        }
        return item.rooms === filterRoom;
      });
      if (!matchesRoom) return false;
    }
    
    // Heating filter - only show properties with matching heating type
    // If heating filter is active, properties without heating should NOT pass
    if (filters.heating?.length) {
      if (!item.heating || !filters.heating.includes(item.heating)) {
        return false;
      }
    }
    
    // Price per m² filter (for apartments, houses)
    if (filters.pricePerM2Min || filters.pricePerM2Max) {
      const pricePerM2 = item.squareMeters > 0 ? item.price / item.squareMeters : 0;
      if (filters.pricePerM2Min && pricePerM2 < filters.pricePerM2Min) return false;
      if (filters.pricePerM2Max && pricePerM2 > filters.pricePerM2Max) return false;
    }
    
    // Price per ar filter (for land plots - plotArea is in m², 1 ar = 100 m²)
    if (filters.pricePerArMin || filters.pricePerArMax) {
      const plotAreaAr = (item.plotArea || item.squareMeters) / 100; // Convert m² to ars
      const pricePerAr = plotAreaAr > 0 ? item.price / plotAreaAr : 0;
      if (filters.pricePerArMin && pricePerAr < filters.pricePerArMin) return false;
      if (filters.pricePerArMax && pricePerAr > filters.pricePerArMax) return false;
    }
    
    if (filters.features?.length) {
      for (const feature of filters.features) {
        if (!item.features[feature]) return false;
      }
    }
    
    // Only with images filter - proverava da li postoje validne slike (ne prazni stringovi)
    if (filters.onlyWithImages) {
      const validImages = item.images?.filter(img => img && img.trim() !== '') || [];
      if (validImages.length === 0) {
        return false;
      }
    }
    
    // Urgent only filter
    if (filters.urgentOnly && !item.isUrgent) {
      return false;
    }
    
    return true;
  });
  
  // Sort: urgent listings first
  return filtered.sort((a, b) => {
    if (a.isUrgent && !b.isUrgent) return -1;
    if (!a.isUrgent && b.isUrgent) return 1;
    return 0;
  });
}
